/**
 * progressRenderers — shared cell renderers + formatters used by both the
 * dashboard's PackagesAnalysisGrid and the Work Schedule list/drill so the
 * Physical / Financial progress bars and the drill-down columns look identical
 * everywhere. Pure presentational helpers — no data fetching.
 */

// Format a rupee amount into crores (₹1 Cr = 1,00,00,000) — matches the
// convention used across the dashboard grids.
export const fmtCr = (val: any) => {
  const cr = (Number(val) || 0) / 10000000;
  return `₹${cr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Cr`;
};

export const WORK_STATUS_COLORS: Record<string, string> = {
  AWARDED: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-green-100 text-green-700',
  INPRG: 'bg-green-100 text-green-700',
  WOPND: 'bg-teal-100 text-teal-700',
  NOT_STARTED: 'bg-gray-100 text-gray-700',
  DELAYED: 'bg-red-100 text-red-700',
};

// Combined Planned/Current bar for the drill-down levels — one column instead
// of two. The full track IS the planned scope (amber fills it whenever there's
// a plan); green overlays it showing how much of that plan is done (cur / plan).
// So a component that's 9% done of an 11% plan shows a nearly-full green bar with
// a small amber tail. The labels keep the raw weightage / done numbers.
export const renderPlanCur = (plan: number, cur: number) => {
  const p = Math.max(0, Math.min(100, Math.round(Number(plan) || 0)));
  const c = Math.max(0, Math.min(100, Math.round(Number(cur) || 0)));
  // The Plan bar represents the FULL planned scope for this row, so it always
  // fills the track when there's a plan. The Current bar shows how much of that
  // plan is done (cur / plan), so a row 1% done of an 11% plan fills ~9%.
  // The labels still show the raw Plan % and Current % values.
  const planWidth = p > 0 ? 100 : 0;
  const curWidth = p > 0 ? Math.max(0, Math.min(100, (c / p) * 100)) : 0;
  // Two stacked bars (matching the Physical/Financial style): Plan (amber) on
  // top, Current (green) below, with the label + % inside each. Gray track so
  // empty bars read as empty.
  const bar = (label: string, labelPct: number, barWidth: number, fill: string, text: string) => (
    <div className="relative w-full h-[13px] bg-gray-100 rounded-full overflow-hidden" title={`${label} ${labelPct}%`}>
      <div className={`absolute inset-y-0 left-0 rounded-full ${fill}`} style={{ width: `${barWidth}%` }} />
      <div className={`absolute inset-0 flex items-center justify-between px-1.5 text-[8px] leading-none ${text}`}>
        <span className="font-semibold uppercase tracking-wide">{label}</span>
        <span className="font-bold">{labelPct}%</span>
      </div>
    </div>
  );
  return (
    <div className="w-full space-y-1">
      {bar('Plan', p, planWidth, 'bg-gradient-to-r from-amber-300 to-amber-400', 'text-amber-900')}
      {bar('Current', c, curWidth, 'bg-gradient-to-r from-emerald-400 to-emerald-600', 'text-emerald-900')}
    </div>
  );
};

// Two stacked progress bars — Target (planned, default 100%) on top and
// Achieved (current done %) below, one under another. The `accent` picks the
// Achieved bar's colour (emerald for physical, sky/blue for financial).
export const renderDualProgress = (
  current: number,
  target: number | null = 100,
  accent: 'emerald' | 'sky' = 'emerald',
) => {
  // Keep the RAW (unrounded) values — the label formatter below decides precision.
  const clampN = (v: number | null | undefined) => Math.max(0, Math.min(100, Number(v) || 0));
  const done = clampN(current);
  const tgt = target == null ? 100 : clampN(target);
  // Format a percent so a tiny-but-POSITIVE value (e.g. 0.003%) never reads as a
  // flat "0%" — it shows "<0.01%" instead. Anything ≥0.01% shows 2 decimals, whole
  // numbers stay clean. This is what lets an early-stage schedule whose target is a
  // fraction of a percent (but has a real ₹ amount due) still read as non-zero.
  const fmtPct = (v: number) => v <= 0 ? '0' : v < 0.01 ? '<0.01' : String(Math.round(v * 100) / 100);
  // Target and Achieved use different hues so they're easy to tell apart, and
  // each metric uses its own pair so Physical and Financial also look distinct:
  //   Physical  → Target amber  / Achieved green
  //   Financial → Target violet / Achieved blue
  // Target is always shown; Achieved is the saturated "how far we've got" bar.
  const TARGET = accent === 'sky'
    ? { fill: 'bg-gradient-to-r from-violet-300 to-violet-400', text: 'text-violet-900' }
    : { fill: 'bg-gradient-to-r from-amber-300 to-amber-400', text: 'text-amber-900' };
  const ACHIEVED = accent === 'sky'
    ? { fill: 'bg-gradient-to-r from-sky-400 to-sky-600', text: 'text-sky-900' }
    : { fill: 'bg-gradient-to-r from-emerald-400 to-emerald-600', text: 'text-emerald-900' };
  // A gray track (so empty bars read as empty, not a coloured pill) with a
  // gradient fill, and the label + % both sitting inside the bar. Dark accent
  // text keeps it readable over both the light track and the fill.
  const bar = (label: string, value: number, style: { fill: string; text: string }) => (
    <div className="relative w-full h-[18px] bg-gray-100 rounded-full overflow-hidden" title={`${label} ${fmtPct(value)}%`}>
      <div className={`absolute inset-y-0 left-0 rounded-full ${style.fill}`} style={{ width: `${value}%` }} />
      <div className={`absolute inset-0 flex items-center justify-between px-2.5 text-[10px] leading-none ${style.text}`}>
        <span className="font-semibold uppercase tracking-wide">{label}</span>
        <span className="font-bold">{fmtPct(value)}%</span>
      </div>
    </div>
  );
  return (
    <div className="w-full space-y-1.5">
      {bar('Target', tgt, TARGET)}
      {bar('Achieved', done, ACHIEVED)}
    </div>
  );
};
